/**
 * Putting a picked staple at the top of the food results (food staples plans 4b and 4c).
 *
 * Picking a staple suggestion searches the staple's short name and then shows that staple first. It
 * is usually among the results already; when it is not, it comes from the device's own copy of the
 * staples (plan 4c, #5625) or, failing that, from a read by id against the server.
 *
 * The rules live here rather than in `foods.ts` so they can be unit-tested — the page is an Angular
 * component and the client's unit tests are plain node tests, which is why `foods-filters.ts`,
 * `food-pick-guard.ts` and `foods-ready-guard.ts` are separate modules too.
 */

import { Food, FoodHeader, Nutrients } from '@cf-shared/data-model/food';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { buildFoodHeader } from 'services/food-header-builder';

/**
 * A row in the results list.
 *
 * `FoodForUI` in `foods.ts` is this plus the fields the food card fills in as it renders. Only
 * `measuresLoading` is named here, because a server row sets it and this module builds those.
 */
export interface StapleResultRow extends FoodHeader {
  measuresLoading?: boolean;
}

/** what this module needs to turn a food into a row, so a test can supply plain functions */
export interface StapleRowDeps {
  /** turns the food's stored nutrients into the numbers the list shows */
  formatNutrients: (nutrients: Nutrients) => UiDisplayNutrient[];
}

// A staple read off the DEVICE is built by `stapleDeviceRow` in `services/staple-db-snapshot.ts`,
// NOT here. This module had its own copy for one review round, and it had already drifted: it
// passed the document's `status` straight through, and no staple document carries that field. The
// page imports that function directly.

/**
 * One staple read by id from the SERVER, as a row for the results list (food staples plan 4b).
 *
 * `score: 0` is what marks it a server row rather than a cached food. `waitForMeasures` is true when
 * the list is showing values per serving, where this row waits for its measures like every other
 * server row.
 */
export function serverStapleRow(staple: Food, waitForMeasures: boolean, deps: StapleRowDeps): StapleResultRow {
  const row: StapleResultRow = {
    ...buildFoodHeader(staple, { formatNutrients: deps.formatNutrients, status: staple.status }),
    altNames: staple.altNames,
    score: 0,
  };
  if (waitForMeasures) {
    row.measuresLoading = true;
  }
  return row;
}

/**
 * The results with `stapleId` first when it is already among them.
 *
 * Answers `undefined` when it is not there, which is the caller's signal to go and find it.
 */
export function moveStapleToFront<T extends { _id?: string }>(results: T[], stapleId: string): T[] | undefined {
  const index = results.findIndex(food => food?._id === stapleId);
  if (index < 0) {
    return undefined;
  }
  return [results[index], ...results.slice(0, index), ...results.slice(index + 1)];
}
